import * as Sentry from "@sentry/react";

export const initSentry = () => {
  // Initialize Sentry if DSN is configured (skip in development)
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const isDev = import.meta.env.DEV;

  if (dsn && !isDev) {
    Sentry.init({
      dsn,
      integrations: [Sentry.browserTracingIntegration()],
      // Higher sample rates in development for testing
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
      replaysSessionSampleRate: import.meta.env.PROD ? 0.01 : 1.0,
      replaysOnErrorSampleRate: 1.0,
      environment: import.meta.env.MODE,
      // Enable debug mode in development
      debug: !import.meta.env.PROD,
      beforeSend(event, hint) {
        // Filter out non-actionable errors (only in production)
        if (import.meta.env.PROD && event.exception) {
          const error = hint.originalException;
          // Skip network errors that are user's connection issues
          if (error instanceof Error && error.message.includes("Failed to fetch")) {
            return null;
          }
        }
        return event;
      },
    });

    // Defer Replay integration to avoid blocking initial render (PageSpeed)
    if (typeof window !== "undefined") {
      const addReplay = () => {
        Sentry.addIntegration(
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          })
        );
      };
      if ("requestIdleCallback" in window) {
        (window as Window).requestIdleCallback(addReplay, { timeout: 3000 });
      } else {
        setTimeout(addReplay, 3000);
      }
    }

    // Only log Sentry initialization in development
    if (import.meta.env.DEV) {
      console.log(`[Sentry] Initialized in ${import.meta.env.MODE} mode`);
    }
  } else if (import.meta.env.DEV) {
    // In dev, Sentry is disabled by design; only warn if DSN is missing for prod
    if (!dsn) {
      console.warn("[Sentry] DSN not configured - error tracking disabled");
    }
  }
};

// User context helper
export const setSentryUser = (userId: string, email?: string) => {
  Sentry.setUser({ id: userId, email });
};

export const clearSentryUser = () => {
  Sentry.setUser(null);
};

// Custom error boundary
export const SentryErrorBoundary = Sentry.ErrorBoundary;
